/**
 * StatusBadge - Status indicator pill (Aprobado/Revisar/Riesgo)
 *
 * Pattern from previous version screenshots:
 * - Small pill with text
 * - Color coded by status
 * - Used in OpportunityCard headers
 */

export type StatusType = 'approved' | 'review' | 'risk' | 'pending' | 'coming-soon';

interface StatusBadgeProps {
  status: StatusType;
  /** Override default label */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Language for default labels */
  language?: 'en' | 'es';
  className?: string;
}

const statusConfig: Record<StatusType, {
  labelEn: string;
  labelEs: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  approved: {
    labelEn: 'Approved',
    labelEs: 'Aprobado',
    bgColor: 'bg-success-500/15',
    textColor: 'text-success-400',
    borderColor: 'border-success-500/30',
  },
  review: {
    labelEn: 'Review',
    labelEs: 'Revisar',
    bgColor: 'bg-warning-500/15',
    textColor: 'text-warning-400',
    borderColor: 'border-warning-500/30',
  },
  risk: {
    labelEn: 'Risk',
    labelEs: 'Riesgo',
    bgColor: 'bg-danger-500/15',
    textColor: 'text-danger-400',
    borderColor: 'border-danger-500/30',
  },
  pending: {
    labelEn: 'Pending',
    labelEs: 'Pendiente',
    bgColor: 'bg-surface-600/50',
    textColor: 'text-text-secondary',
    borderColor: 'border-surface-500/30',
  },
  'coming-soon': {
    labelEn: 'Coming Soon',
    labelEs: 'Próximamente',
    bgColor: 'bg-primary-500/15',
    textColor: 'text-primary-400',
    borderColor: 'border-primary-500/30',
  },
};

export function StatusBadge({
  status,
  label,
  size = 'sm',
  language = 'en',
  className = '',
}: StatusBadgeProps) {
  const config = statusConfig[status];
  const displayLabel = label || (language === 'es' ? config.labelEs : config.labelEn);

  const sizeClasses = size === 'sm'
    ? 'px-2 py-0.5 text-xs'
    : 'px-2.5 py-1 text-sm';

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium
        ${config.bgColor} ${config.textColor} ${config.borderColor} border
        ${sizeClasses}
        ${className}
      `}
    >
      {displayLabel}
    </span>
  );
}

/**
 * StatusIcon - Just the icon for inline use
 */
export function StatusIcon({ status, className = '' }: { status: StatusType; className?: string }) {
  const icons: Record<StatusType, string> = {
    approved: '✓',
    review: '⚠',
    risk: '✕',
    pending: '○',
    'coming-soon': '◐',
  };

  const config = statusConfig[status];

  return (
    <span className={`${config.textColor} ${className}`}>
      {icons[status]}
    </span>
  );
}
